import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
  Cascader,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Form,
  Tooltip,
  Col,
  Spin,
  Typography,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import styles from '../filter-profile-details.module.sass';
import { infoNotification, showNotification } from '../../Notifications';
import { LoadingOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { STATUS } from '../../../redux/constants';

function FilterField(props, ref) {
  const {
    field,
    options,
    optionsByIndicator,
    onChange,
    canDelete,
    onDelete,
    binary_operator,
    indicator,
    operator,
    hideBinaryOperator,
    indicatorsMap,
    resetSpecificFilter,
    loadSpecificFilter,
    loadTagOptions,
    autoCompleteTagOptions,
    tagOptions,
    tagOptionsStatus,
    resetTagOptions,
    specificFilterSettingsStatus,
    specificFilterIndicator,
    expressionIndex,
    conditionIndex,
    setNumberInputError,
    numberInputError,
  } = props;

  const { Option } = Select;
  const [form] = Form.useForm();
  const indicatorObject = optionsByIndicator[field.indicator];
  const [notified, setNotified] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [opacity, setOpacity] = useState(false);
  const [multiselectDisabled, setMultiselectDisabled] = useState(false);
  const [multiDropdownOptions, setMultiDropdownOptions] = useState([]);
  const [tagnameInputValue, setTagnameInputValue] = useState('');
  const { Text } = Typography;

  useEffect(() => {
    setTimeout(() => {
      setAnimate(true);
      setOpacity(true);
    }, 10);
  }, []);

  useEffect(() => {
    if (indicatorObject?.indicatorName === 'ASN') {
      setMultiDropdownOptions(specificFilterIndicator);
    } else {
      setMultiDropdownOptions(indicatorObject?.options);
    }

    if (indicatorObject?.indicatorName === 'Tag name') {
      setMultiDropdownOptions(tagOptions);
    }
  }, [specificFilterIndicator, indicatorObject, tagOptions, field?.value]);

  useEffect(() => {
    if (indicatorObject?.indicatorName === 'ASN') {
      resetSpecificFilter();
      loadSpecificFilter('asn', 'json');
    }
    if (indicatorObject?.indicatorName === 'Tag name') {
      resetTagOptions();
      loadTagOptions();
    }
  }, [indicatorObject?.indicatorName]);

  useEffect(() => {
    //replace options after two characters in tag multiselect
    if (tagnameInputValue?.length > 1) {
      resetTagOptions();
      autoCompleteTagOptions(tagnameInputValue);
    } else if (tagnameInputValue?.length === 0) {
      resetTagOptions();
      loadTagOptions();
    }
  }, [tagnameInputValue]);

  const handleDeleteCondition = () => {
    setOpacity(false);
    setTimeout(() => {
      onDelete();
    }, 300);
  };

  const returnFieldValue = (type) => {
    if (type === 'boolean') {
      return null;
    } else if (type === 'date') {
      return '';
    } else if (type === 'dateTime') {
      return '';
    } else {
      return [];
    }
  };

  const handleIndicatorChange = (indicator) => {
    let secondLevelIndicator = indicator[1];

    const defaultOperator = (optionsByIndicator[secondLevelIndicator]
      ?.operators || [])[0];
    const operator = defaultOperator?.value;
    const value = returnFieldValue(
      optionsByIndicator[secondLevelIndicator]?.type,
    );

    field.type = optionsByIndicator[secondLevelIndicator]?.type;
    onChange(
      { ...field, indicator: secondLevelIndicator, operator, value },
      'indicator',
    );
    form.setFieldsValue({ indicator: secondLevelIndicator, operator, value });
  };

  const handleOperatorChange = (operator) => {
    onChange({ ...field, operator }, 'operator');
  };

  const handleValueChange = (value) => {
    //reset tracking value when a new tag is added to the multiselect
    if (indicatorObject?.indicatorName === 'Tag name') {
      setTagnameInputValue('');
    }

    value?.length >= 50
      ? setMultiselectDisabled(true)
      : setMultiselectDisabled(false);
    onChange({ ...field, value }, 'value');
  };

  const handleNumberValueChange = (e) => {
    const value = e?.target?.value;
    const fieldValue = field?.value?.toString();

    if (value === '') {
      setNumberInputError(true);
      return;
    }
    if (
      !fieldValue?.includes('e') &&
      !fieldValue?.includes('-') &&
      !fieldValue?.includes('+')
    ) {
      setNumberInputError(false);
    }

    value?.length >= 50
      ? setMultiselectDisabled(true)
      : setMultiselectDisabled(false);
    onChange({ ...field, value }, 'value');
  };

  const handleTagAutocomplete = (e) => {
    if (e.key === 'Meta') {
      //if the user is using Ctrl+A to select value and type over it reset the transfer value
      setTagnameInputValue('');
      return;
    }

    if (e?.key?.length === 1) {
      setTagnameInputValue(tagnameInputValue + e.key);
    } else if (e?.key === 'Backspace') {
      setTagnameInputValue(tagnameInputValue?.slice(0, -1));
    }
  };

  const handleLosingFocus = (e) => {
    setTagnameInputValue('');
  };

  const handleBinaryIndicatorChange = (binary_operator) => {
    onChange({ ...field, binary_operator }, 'binary_operator');
  };

  const filterOption = (inputValue, option) => {
    return option.label
      ?.toString()
      .toLowerCase()
      .includes(inputValue?.toString().toLowerCase());
  };

  const operators = indicatorObject?.operators || [];

  const checkRemovedOptions = (options) => {
    if (field?.indicator === 'tags__name') return options;
    if (field?.value != null && options?.length > 0) {
      const found = field?.value?.map((v) =>
        options?.find((f) => f.value == v),
      );
      const removed = field?.value?.filter(
        (v) => !found?.map((fv) => fv?.value)?.includes(v),
      );
      const removedOptions = removed?.map((r) => {
        if (field?.indicator === 'ioc_asn') {
          return { value: r, label: r };
        } else {
          return { value: r, label: 'REMOVED' };
        }
      });
      return [...options, ...removedOptions];
    }
  };

  const handlePopupScroll = (e) => {
    const target = e.target;
    // Check if the user has scrolled to the bottom
    if (target.scrollTop + target.clientHeight === target.scrollHeight) {
      // Logic when scrolled to the bottom
      // Need to implement paging here
    }
  };

  const renderValueField = () => {
    if (field.type === 'date') {
      field.value = moment(field.value)?.tz('UTC');
    }

    switch (indicatorObject?.type) {
      case 'multi-dropdown':
        if (
          specificFilterSettingsStatus === STATUS.LOADED ||
          tagOptionsStatus === STATUS.LOADED ||
          indicatorObject?.options.length > 0
        ) {
          return (
            <Select
              mode={
                indicatorObject?.indicatorName === 'ASN' ? 'tags' : 'multiple'
              }
              className={{ [styles.optionsDisabled]: multiselectDisabled }}
              allowClear
              placeholder="Select Option"
              onClick={
                indicatorObject?.indicatorName === 'Tag name'
                  ? handleLosingFocus
                  : null
              }
              onKeyUp={
                indicatorObject?.indicatorName === 'Tag name'
                  ? handleTagAutocomplete
                  : null
              }
              onBlur={
                indicatorObject?.indicatorName === 'Tag name'
                  ? handleLosingFocus
                  : null
              }
              onChange={handleValueChange}
              getPopupContainer={(trigger) => trigger.parentNode}
              //onPopupScroll={handlePopupScroll}
              options={checkRemovedOptions(multiDropdownOptions) || []}
              filterOption={filterOption}
            />
          );
        } else {
          return (
            <Spin
              indicator={
                <LoadingOutlined className={styles.multiSelectSpinner} />
              }
            />
          );
        }

      case 'boolean':
        return (
          <Select
            placeholder="Select Option"
            onChange={handleValueChange}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            <Option value={true}>True</Option>
            <Option value={false}>False</Option>
          </Select>
        );

      case 'date':
      case 'datetime':
        let fieldValue = moment(field.value).tz('UTC');

        return (
          <DatePicker
            value={fieldValue}
            onChange={(dateM) =>
              handleValueChange(
                dateM
                  ?.utcOffset(0)
                  ?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                  ?.toISOString(),
              )
            }
            getPopupContainer={(trigger) => trigger.parentNode}
          />
        );

      case 'dropdown':
        const dropdownOptions = indicatorObject?.options || [];
        return (
          <Select
            options={dropdownOptions}
            placeholder="Select Option"
            onChange={handleValueChange}
            getPopupContainer={(trigger) => trigger.parentNode}
            showSearch={true}
          />
        );

      case 'number':
        const range = indicatorObject?.range || { min: 0, max: 100 };
        return (
          <InputNumber
            placeholder="Value"
            type="number"
            value={field?.value}
            min={range.min}
            max={range.max}
            onKeyUp={(e) => handleNumberValueChange(e)}
            status={numberInputError ? 'error' : ''}
          />
        );

      case 'text':
      default:
        return (
          <Input
            placeholder="Value"
            disabled={!indicator}
            onChange={(e) => handleValueChange(e.target.value)}
          />
        );
    }
  };

  function filter(inputValue, path) {
    return path?.some(
      (option) =>
        option?.label
          ?.toString()
          ?.toLowerCase()
          ?.indexOf(inputValue?.toString()?.toLowerCase()) > -1,
    );
  }

  function cascadeValueLookup(indicator) {
    const index = indicatorsMap?.findIndex((p) => p.indicator === indicator);
    return indicatorsMap?.[index]?.path;
  }

  const handleRequiredValue = (operator) => {
    const relativeDates = ['Today', 'Yesterday', 'Last 7 Days'];
    if (relativeDates?.includes(operator)) {
      field.value = moment()?.tz('UTC');
    }
    return relativeDates?.includes(operator);
  };

  //@TODO: remove this when users change filter profiles to dropdown
  const is_operator_invalid =
    operator == 'Contains' &&
    ['feed__name', 'feed__vendor', 'tags__name'].includes(indicator);
  if (is_operator_invalid && !notified) {
    setNotified(true);
    showNotification(
      infoNotification({
        statusCode: 100,
        message:
          'Please change your filter profile to match one of the values in the list',
      }),
    );
  }

  return (
    <Col
      xs={24}
      className={cx(
        styles.expressionColumn,
        { [styles.animate]: animate },
        { [styles.opacity]: opacity },
      )}
      data-highlight-id={`${expressionIndex}-${conditionIndex}-condition`}
    >
      <Form
        ref={ref}
        form={form}
        name="logical_expression_form"
        autoComplete="off"
        initialValues={field}
      >
        {!hideBinaryOperator && (
          <Tooltip placement="topLeft" title={binary_operator} color="black">
            <Select
              className={styles.binaryOperator}
              value={binary_operator}
              onChange={handleBinaryIndicatorChange}
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              <Select.Option value="AND">AND</Select.Option>
              <Select.Option value="OR">OR</Select.Option>
            </Select>
          </Tooltip>
        )}

        <div className={styles.indicator}>
          <Form.Item
            name="indicator"
            rules={[
              {
                required: true,
                message: 'Missing indicator',
              },
            ]}
          >
            <Tooltip
              placement="topLeft"
              title={indicatorObject && indicatorObject.indicatorName}
              color="black"
            >
              <Cascader
                data-cy="filterFieldIndicator"
                options={options}
                onChange={handleIndicatorChange}
                placeholder="Select Indicator"
                showSearch={{ filter }}
                value={cascadeValueLookup(indicator)}
                getPopupContainer={(trigger) => trigger.parentNode}
              />
            </Tooltip>
          </Form.Item>

          {canDelete && (
            <Tooltip title="Remove condition from the expression">
              <DeleteOutlined
                className={styles.delete}
                onClick={handleDeleteCondition}
              />
            </Tooltip>
          )}
        </div>

        <div className={styles.expressionValue}>
          <div className={styles.operator}>
            <Form.Item
              name="operator"
              rules={[{ required: true, message: 'Missing operator' }]}
            >
              <Tooltip placement="topLeft" title={operator} color="black">
                <Select
                  data-cy="filterFieldOperator"
                  placeholder="Select operator"
                  options={operators}
                  value={operator}
                  onChange={handleOperatorChange}
                  disabled={!indicator}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  status={is_operator_invalid ? 'error' : ''}
                />
              </Tooltip>
            </Form.Item>
          </div>

          <div className={styles.value}>
            <Form.Item
              data-cy="filterFieldValue"
              style={handleRequiredValue(operator) ? { display: 'none' } : {}}
              name="value"
              rules={[
                {
                  required: !handleRequiredValue(operator),
                  message: 'Missing or invalid value',
                },
              ]}
            >
              {renderValueField()}
            </Form.Item>
            {numberInputError && field?.value?.length === 0 && (
              <Text className={styles.customError}>
                Missing or invalid value
              </Text>
            )}
          </div>
        </div>
      </Form>
    </Col>
  );
}
export default React.forwardRef(FilterField);
